import { isHotkeyToOpenWidget } from '@uw/common-components';
import { useCallback, useEffect } from 'preact/hooks';
import { useApplicationSetup } from 'contexts/use-application-setup';
import { stateStore } from '../global/state.store';
import { Trigger } from '../types/trigger';

export const useKeyboardEvents = (config: ReturnType<typeof useApplicationSetup>) => {
  const copyTextToClipboard = useCallback((text: string) => {
    const textArea = document.createElement('textarea');

    //
    // *** This styling is an extra step which is likely not required. ***
    //
    // Why is it here? To ensure:
    // 1. the element is able to have focus and selection.
    // 2. if element was to flash render it has minimal visual impact.
    // 3. less flakyness with selection and copying which **might** occur if
    //    the textarea element is not visible.
    //
    // The likelihood is the element won't even render, not even a
    // flash, so some of these are just precautions. However in
    // Internet Explorer the element is visible whilst the popup
    // box asking the user for permission for the web page to
    // copy to the clipboard.
    //

    // Place in top-left corner of screen regardless of scroll position.
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';

    // Ensure it has a small width and height. Setting to 1px / 1em
    // doesn't work as this gives a negative w/h on some browsers.
    textArea.style.width = '2em';
    textArea.style.height = '2em';

    // We don't need padding, reducing the size if it does flash render.
    textArea.style.padding = '0';

    // Clean up any borders.
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';

    // Avoid flash of white box if rendered for any reason.
    textArea.style.background = 'transparent';

    textArea.value = text;

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'successful' : 'unsuccessful';
      console.log(`Copying text command was ${msg}`);
    } catch (err) {
      console.log('Oops, unable to copy');
    }

    document.body.removeChild(textArea);
  }, []);

  const keyDown = useCallback(
    (event) => {
      if (event.keyCode === 76 && event.ctrlKey) {
        // CTRL + L
        event.preventDefault();
        if (config.trigger === Trigger.Main) {
          stateStore.state.next('main.login');
        }
      }

      if (isHotkeyToOpenWidget(config, event) || event.keyCode === 27) {
        // check if tooltip in widget is focused
        const activeElement = document.activeElement as HTMLButtonElement;
        const isTooltipFocused = activeElement?.classList?.contains('uwaw-main-tooltip-icon');
        if (isTooltipFocused && event.keyCode === 27) {
          // hide tooltip on first Esc key press
          activeElement.blur();
        } else {
          // CTRL + U - default; or Escape
          event.preventDefault();
          config.close();
        }
      }

      if (event.keyCode === 75 && event.ctrlKey) {
        // CTRL + K
        event.preventDefault();
        const copyText = config.code;
        copyTextToClipboard(copyText);
      }

      if (event.keyCode === 74 && event.ctrlKey) {
        // CTRL + J
        event.preventDefault();
        const copyText = config.config.current.services.userId;
        copyTextToClipboard(copyText);
      }
    },
    [copyTextToClipboard, config],
  );

  useEffect(() => {
    if (config.isOpen) {
      window.addEventListener('keydown', keyDown);
    }

    return () => {
      window.removeEventListener('keydown', keyDown);
    };
  }, [keyDown, config?.isOpen]);
};
